import {
  useCallback,
  useRef,
  useState,
  useEffect,
  ReactElement,
  FocusEventHandler,
} from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/lib/hooks/redux";
import Cleave from "cleave.js/react";
import { ReactInstanceWithCleave } from "cleave.js/react/props";
import { getCountrySpecifications } from "ibantools";

import { IBANFieldConfig } from "@evergiving/common/dist/es2015/form";

import { RootState } from "@/lib/redux/types";
import { setIBAN, resetFormErrors } from "@/lib/redux/actions";
import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { useFieldValidationErrors } from "@/lib/hooks/useFieldValidationErrors";
import { InputFieldConfig } from "@/lib/misc/InputFieldConfig";
import { FieldError } from "@/lib/components/FieldError";

function normalizeFieldValue(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed.length > 0) {
    return trimmed; // .replace(/\D/g, "");
  }
  return undefined;
}

function hasOwnProperty<X extends {}, Y extends PropertyKey>(
  obj: X,
  prop: Y
): obj is X & Record<Y, unknown> {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

export function IBANField({
  config,
  autoFocus,
}: {
  config: IBANFieldConfig;
} & InputFieldConfig): ReactElement {
  const name = "directDebitIBAN";
  const required = !!config.requiredFieldErrorText;
  const value = useSelector((state: RootState) => state[name].value);
  const bankName = useSelector((state: RootState) =>
    state.ibanAppraisal?.valid ? state.ibanAppraisal.bankName : null
  );
  const [currentValue, setCurrentValue] = useState<string>(value || "");
  const dispatch = useAppDispatch();
  const [isDirty, setIsDirty] = useState(false);

  const fieldValidationErrors = useFieldValidationErrors(config);
  const errorMessages = fieldValidationErrors.forValue(
    normalizeFieldValue(currentValue)
  );
  const anyErrors = errorMessages.length > 0;

  const handleBlur = useCallback<FocusEventHandler<HTMLInputElement>>(() => {
    const newValue = normalizeFieldValue(currentValue);
    dispatch(setIBAN(config, newValue));

    setIsDirty(true);
  }, [config, currentValue, dispatch]);

  const cleaveRef = useRef<ReactInstanceWithCleave | null>(null);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { current } = cleaveRef;
      if (current && hasOwnProperty(event.target, "rawValue")) {
        const { rawValue } = event.target;
        if (typeof rawValue === "string") {
          dispatch(resetFormErrors(name));

          let formattedValue = "";
          rawValue.split("").forEach((character) => {
            if (
              character.match(
                formattedValue.length < 2 ? /[a-zA-Z]/ : /[a-zA-Z0-9]/
              )
            ) {
              formattedValue += character;
            }
          });
          const country = getCountrySpecifications()[
            formattedValue.substring(0, 2)
          ];
          if (country) {
            const { chars } = country;
            if (chars) {
              formattedValue = formattedValue.substring(0, chars);
            }
          }
          if (formattedValue !== rawValue) {
            current.setRawValue(formattedValue);
          }

          setCurrentValue(formattedValue);
        }
      }
    },
    [dispatch, name, cleaveRef]
  );

  const handleInit = useCallback(
    (cleave: ReactInstanceWithCleave) => {
      cleaveRef.current = cleave;
    },
    [cleaveRef]
  );

  useEffect(() => {
    setCurrentValue(value || "");
  }, [value]);

  const errorId = `${name}-errors`;

  const inputField = (
    <>
      {/* eslint-disable jsx-a11y/no-autofocus */}
      {/* ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^ Discussion of this override follows:

       We're only passing through the attribute here.  See DonationDetailsPage for
       a discussion why we would ever want to set this attribute.

       */}
      <Cleave
        pattern="[a-zA-Z]{2}[0-9]+"
        options={{
          blocks: [4, 4, 4, 4, 4, 4, 4, 4],
          uppercase: true,
        }}
        className="textField"
        type="text"
        name="iban"
        placeholder={config.placeholderText}
        value={currentValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onInit={handleInit}
        autoFocus={autoFocus}
        spellCheck="false"
        aria-required={required}
        aria-invalid={(anyErrors && isDirty) || undefined}
        aria-describedby={anyErrors ? errorId : undefined}
      />
      {/* eslint-enable jsx-a11y/no-autofocus */}
      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError id={errorId} messages={errorMessages} />
        ) : null}
      </div>
    </>
  );

  return (
    <LabelledField id={name}>
      <LabelText requiredIndicator={required}>{config.labelText}</LabelText>
      {inputField}
      {bankName}
    </LabelledField>
  );
}
